// if we add much complexity we might want an OAuthManager instead
import DIContainer from '@components/DependencyInjectionContainer.js';
import { getID } from '@components/Utility/GeneralUtil.js';
import { defer, finalize, Observable, of, take, throwError } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { ENV } from '@components/Environment.js';
export function oAuthPopup(parameters, context) {
    return defer(() => {
        const broadcastId = getID("oa_bc"); // shorter name for shorter state token
        const params = { ...parameters, broadcastid: broadcastId };
        const url = new URL(constructAuthUrl(params));
        url.searchParams.set("popup", "1");
        const popup = window.open(url.toString(), "oauthPopup", "width=600,height=600,resizable=yes,scrollbars=yes");
        if (!popup || popup.closed || typeof popup.closed === "undefined") {
            const errMessage = DIContainer
                .getInstance("CaptionManager")
                .getCaption("system_popup_blocked");
            context.alert({ caption: errMessage, modalStyle: "warning" });
            return throwError(() => new Error(errMessage));
        }
        return DIContainer
            .getInstance("BroadcastManager")
            .listen(broadcastId)
            .pipe(take(1), mergeMap((resp) => {
            const err = resp?.payload?._error;
            if (err) {
                context.alert({ caption: err, modalStyle: "error" });
                return throwError(() => new Error(err));
            }
            return of(resp);
        }), finalize(() => {
            try {
                popup.close();
            }
            catch { /* ignore */ }
        }));
    });
}
export function oAuthNative(parameters, context) {
    return defer(() => {
        const url = new URL(constructAuthUrl(parameters));
        url.searchParams.set("native", "1");
        return new Observable(subscriber => {
            let appListenerHandle = null;
            let browserListenerHandle = null;
            let settled = false;
            const cleanup = () => {
                appListenerHandle?.remove().catch(() => { });
                browserListenerHandle?.remove().catch(() => { });
                appListenerHandle = null;
                browserListenerHandle = null;
            };
            (async () => {
                const { App } = await import('@capacitor/app');
                const { Browser } = await import('@capacitor/browser');
                appListenerHandle = await App.addListener('appUrlOpen', async (event) => {
                    console.log('[oAuthNative] appUrlOpen fired:', event.url);
                    if (!event.url.startsWith('com.solutions360.q360mobileapp://oauth-callback'))
                        return;
                    settled = true;
                    cleanup();
                    try {
                        await Browser.close();
                    }
                    catch { /* browser may already be closed */ }
                    // Convert custom scheme to https:// so URL constructor can parse the query parameters
                    const callbackUrl = new URL(event.url.replace('com.solutions360.q360mobileapp://', 'https://'));
                    const error = callbackUrl.searchParams.get('_error');
                    if (error) {
                        console.log('[oAuthNative] callback error:', error);
                        context.alert({ caption: error, modalStyle: "error" });
                        subscriber.error(new Error(error));
                        return;
                    }
                    if (parameters.mode === 'authorize_email') {
                        console.log('[oAuthNative] authorize_email completed');
                        subscriber.next({ payload: { source: 'oauth' } });
                        subscriber.complete();
                        return;
                    }
                    const loginToken = callbackUrl.searchParams.get('logintoken');
                    console.log('[oAuthNative] loginToken present:', !!loginToken);
                    if (!loginToken) {
                        const msg = 'OAuth callback missing login token.';
                        context.alert({ caption: msg, modalStyle: "error" });
                        subscriber.error(new Error(msg));
                        return;
                    }
                    // Exchange the one-time token for a session in the WebView context
                    console.log('[oAuthNative] claiming native login...');
                    DIContainer.getInstance('Request').GET({
                        endpoint: 'authenticate',
                        action: 'claimNativeLogin',
                        requestData: { logintoken: loginToken },
                        initiator: DIContainer.getInstance('Application'),
                        showErrors: true
                    }).subscribe({
                        next: () => {
                            console.log('[oAuthNative] claimNativeLogin success');
                            subscriber.next({ payload: { source: 'oauth' } });
                            subscriber.complete();
                        },
                        error: (e) => {
                            console.log('[oAuthNative] claimNativeLogin error:', e.message);
                            context.alert({ caption: e.message, modalStyle: "error" });
                            subscriber.error(e);
                        }
                    });
                });
                // If the user closes the browser without completing OAuth, error out
                browserListenerHandle = await Browser.addListener('browserFinished', () => {
                    if (!settled) {
                        settled = true;
                        cleanup();
                        subscriber.error(new Error(DIContainer
                            .getInstance("CaptionManager")
                            .getCaption("system_popup_closed")));
                    }
                });
                // Open the system browser after listeners are registered
                await Browser.open({ url: url.toString() });
            })().catch(err => {
                if (!settled) {
                    cleanup();
                    subscriber.error(err);
                }
            });
            return () => { cleanup(); };
        });
    });
}
export function oAuthRedirect(parameters) {
    window.location.href = constructAuthUrl(parameters);
}
function constructAuthUrl(parameters) {
    let action = 'authorizeLogin';
    if (parameters.mode === 'authorize_email') {
        action = 'authorizeEmail';
    }
    const origin = ENV('isNative') ? new URL(ENV('apiRoot')).origin : window.location.origin;
    const url = new URL(`/oauth/login/${action}`, origin);
    // Add all defined params
    for (const [key, value] of Object.entries(parameters)) {
        if (value === undefined || value === null)
            continue;
        url.searchParams.set(key, String(value));
    }
    // Include client type so the callback can set the correct cookie names
    if (ENV('appName')) {
        url.searchParams.set('clienttype', ENV('appName'));
    }
    return url.toString();
}
